import { Fragment } from "react";
import {
  ROMSH_INPUT_HEIGHT_TEXT as HEIGHT_TEXT,
  ROMSH_INPUT_NEUTRAL_TEXT as NEUTRAL_TEXT,
  ROMSH_INPUT_TEXT as TEXT,
} from "@/constants/romshInputText";
import type {
  AnalysisEvidence,
  AnalysisFeature,
  RomshInputRecord,
} from "@/types";
import {
  allInputRtZero,
  buildRomshInputRows,
  classifyRomshInput,
  type RomshInputSource,
  romshInputSource,
  summaryValues,
} from "@/utils/romshInput";

/**
 * RoMSH 입력 성분 표 — 모델에 보낸 성분의 기록 (PLAN 2-9a PR-3).
 *
 * 계약: docs/2026/260923_RoMSH_입력기록_기획.md §3. 플래그(SHOW_ROMSH_INPUTS)와
 * 시연 시나리오 모드는 부모가 판단한다 — 이 컴포넌트는 플래그를 읽지 않는다.
 *
 * 문구·칸은 저장된 기록의 schema·입력값 출처로 고른다(PLAN 2-9d): v1 매칭 점수
 * 기록은 당시 문구 그대로, v2 TIC 높이 기록은 「TIC 신호 비율」 칸과 그 설명,
 * 모르는 출처는 중립 문구(입력값 칸 없음).
 *
 * 값을 다시 계산하지 않고 기록을 옮겨 적는다. 데이터 칸·치환값은
 * `data-testid="romsh-input-data"` 로 표시한다 — 금지 문구 점검(§3-6)이 고정
 * 문구만 검사하도록.
 */

interface RomshInputTableProps {
  evidence: AnalysisEvidence | null | undefined;
  romshStatus: string | null | undefined;
  features: readonly AnalysisFeature[];
  selectedFeatureId: string | null;
  onSelectFeature: (featureId: string) => void;
}

function Data({ children }: { children: React.ReactNode }) {
  return <span data-testid="romsh-input-data">{children}</span>;
}

function Info({ text }: { text: string }) {
  return (
    <span
      role="img"
      aria-label={text}
      title={text}
      className="text-xs opacity-50 cursor-help"
    >
      ⓘ
    </span>
  );
}

/** 문구 안의 치환값(기록에서 읽은 값)을 데이터로 표시한다. */
function WithData({ text, data }: { text: string; data: (string | null)[] }) {
  const tokens = data.filter((d): d is string => !!d && text.includes(d));
  if (tokens.length === 0) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest.length > 0) {
    let first = -1;
    let tok = "";
    for (const t of tokens) {
      const i = rest.indexOf(t);
      if (i >= 0 && (first < 0 || i < first)) {
        first = i;
        tok = t;
      }
    }
    if (first < 0) {
      parts.push(<Fragment key={key++}>{rest}</Fragment>);
      break;
    }
    if (first > 0)
      parts.push(<Fragment key={key++}>{rest.slice(0, first)}</Fragment>);
    parts.push(<Data key={key++}>{tok}</Data>);
    rest = rest.slice(first + tok.length);
  }
  return <>{parts}</>;
}

/** 저장된 출처별 설명 문구 — 제목·JSD·선정·요약값 ⓘ 와 각주 1. */
function sourceTexts(
  source: RomshInputSource,
  record: RomshInputRecord,
  rule: string,
): {
  titleInfo: string;
  jsdInfo: string;
  reasonInfo: string;
  footnote1: string;
  summaryInfo: string;
} {
  const mr = record.model_received;
  const kind: "request" | "abundance" | "uniform" =
    mr?.fuel_proxy_source === "request"
      ? "request"
      : mr?.fuel_proxy_basis === "abundance"
        ? "abundance"
        : "uniform";
  if (source === "tic_height") {
    return {
      titleInfo: HEIGHT_TEXT.titleInfo(rule),
      jsdInfo: HEIGHT_TEXT.jsdInfo,
      reasonInfo: HEIGHT_TEXT.reasonInfo,
      footnote1: HEIGHT_TEXT.footnote1,
      summaryInfo: HEIGHT_TEXT.summaryInfo[kind],
    };
  }
  if (source === "neutral") {
    return {
      titleInfo: NEUTRAL_TEXT.titleInfo(rule),
      jsdInfo: NEUTRAL_TEXT.jsdInfo,
      reasonInfo: NEUTRAL_TEXT.reasonInfo,
      footnote1: NEUTRAL_TEXT.footnote1,
      summaryInfo: NEUTRAL_TEXT.summaryInfo[kind],
    };
  }
  const withJsd = record.components.some((c) => c.jsd_distance !== null);
  return {
    titleInfo:
      rule === "v2.1" ? TEXT.titleInfoV21(withJsd) : TEXT.titleInfoOther(rule),
    jsdInfo: TEXT.jsdInfo,
    reasonInfo: TEXT.reasonInfo,
    footnote1: TEXT.footnote1,
    summaryInfo:
      kind === "request"
        ? TEXT.summaryInfoRequest
        : kind === "abundance"
          ? TEXT.summaryInfoAbundance
          : TEXT.summaryInfoOther,
  };
}

/** v2 입력에서 뺀 후보 — 사유별 건수(기록 순서대로, 모르는 사유는 기타). */
function excludedBreakdown(record: RomshInputRecord): {
  count: number;
  info: string;
} {
  const list =
    record.schema === "romsh_input_v2"
      ? (record.selection?.excluded ?? [])
      : [];
  const counts = new Map<string, number>();
  for (const e of list) {
    const label =
      HEIGHT_TEXT.excludedReason[e.reason] ?? HEIGHT_TEXT.excludedOther;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const parts = [...counts].map(([label, k]) => `${label} ${k}건`).join(" · ");
  return { count: list.length, info: HEIGHT_TEXT.excludedInfo(parts) };
}

function stateData(
  record: RomshInputRecord | null,
  evidence: unknown,
): (string | null)[] {
  let code = record?.failure?.code ?? null;
  let schema: string | null = null;
  let recordErrorCode: string | null = null;
  const params = (evidence as AnalysisEvidence | null)?.parameters;
  const raw = params && "romsh_input" in params ? params.romsh_input : null;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (typeof o.schema === "string") schema = o.schema;
    // 표 없이 문구만 보이는 상태(#9 build 등)는 view.record 가 null 이다 — 치환값
    // {code} 를 원 기록에서 읽어 데이터로 표시한다(기획 §3-6).
    const f = o.failure;
    if (
      code === null &&
      f &&
      typeof f === "object" &&
      typeof (f as Record<string, unknown>).code === "string"
    ) {
      code = (f as Record<string, unknown>).code as string;
    }
    const re = o.record_error;
    if (
      re &&
      typeof re === "object" &&
      typeof (re as Record<string, unknown>).code === "string"
    ) {
      recordErrorCode = (re as Record<string, unknown>).code as string;
    }
  }
  return [code, schema, recordErrorCode];
}

export default function RomshInputTable({
  evidence,
  romshStatus,
  features,
  selectedFeatureId,
  onSelectFeature,
}: RomshInputTableProps) {
  const view = classifyRomshInput(evidence, romshStatus);
  if (view.message === null && !view.showTable) return null; // #3 · #8

  const record = view.record;
  const data = stateData(record, evidence);

  if (!view.showTable || !record) {
    return (
      <section
        data-testid="romsh-input"
        className="border-t border-gray-200 pt-2"
      >
        <p
          data-testid="romsh-input-state"
          className="text-xs text-gray-600 leading-snug"
        >
          <WithData text={view.message ?? ""} data={data} />
        </p>
      </section>
    );
  }

  const rows = buildRomshInputRows(record, features, view.showModelSide);
  const sel = record.selection;
  const N = sel?.pool.length ?? 0;
  const n = record.components.length;
  const k = sel?.excluded_no_smiles.length ?? 0;
  const rule = sel?.rule ?? TEXT.none;
  const source = romshInputSource(record);
  const { titleInfo, jsdInfo, reasonInfo, footnote1, summaryInfo } =
    sourceTexts(source, record, rule);
  const showSignal = source === "tic_height";
  const summaryText =
    source === "tic_height"
      ? HEIGHT_TEXT
      : source === "neutral"
        ? NEUTRAL_TEXT
        : TEXT;
  // v2 TIC 높이 기록은 구조 정보 없는 관찰도 selection.excluded 에 함께 있다 —
  // 제외 건수는 그 목록 하나로만 센다(두 번 세지 않게).
  const excludedText =
    source === "neutral" ? NEUTRAL_TEXT.summaryExcluded : TEXT.summaryExcluded;
  const unit = source === "match_score" ? "종" : "건";
  const notRep = showSignal ? excludedBreakdown(record) : null;
  const summary = view.showModelSide ? summaryValues(record) : null;

  return (
    <section
      data-testid="romsh-input"
      className="border-t border-gray-200 pt-2 space-y-1.5"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-800">
            {TEXT.title}
          </span>
          <Info text={titleInfo} />
        </div>
        <span className="text-[11px] text-gray-400">{TEXT.titleSide}</span>
      </div>

      {view.message && (
        <p
          data-testid="romsh-input-state"
          className="text-xs text-slate-700 leading-snug"
        >
          <WithData text={view.message} data={data} />
        </p>
      )}

      {sel && (
        <p data-testid="romsh-input-summary" className="text-xs text-gray-600">
          {N > n ? (
            <WithData
              text={summaryText.summaryOver(N, n, rule)}
              data={[`${N}${unit}`, `${n}개`, rule]}
            />
          ) : (
            <WithData
              text={summaryText.summaryWithin(N, rule)}
              data={[`${N}${unit}`, rule]}
            />
          )}
          {k > 0 && !showSignal && (
            <WithData text={excludedText(k)} data={[`${k}${unit}`]} />
          )}
          {notRep && notRep.count > 0 && (
            <>
              <WithData
                text={HEIGHT_TEXT.summaryNotRepresentative(notRep.count)}
                data={[`${notRep.count}건`]}
              />{" "}
              <Info text={notRep.info} />
            </>
          )}
        </p>
      )}

      <p className="text-[11px] text-gray-500">
        {allInputRtZero(record) ? TEXT.subtitleAllZeroRt : TEXT.subtitle}
      </p>

      <div className="overflow-x-auto">
        <table
          data-testid="romsh-input-table"
          className={`${showSignal ? "min-w-[680px]" : "min-w-[600px]"} w-full text-xs`}
        >
          <thead>
            <tr className="text-left text-gray-500">
              <th className="w-3" />
              <th className="font-medium py-1 pr-2 whitespace-nowrap">
                {TEXT.columns.rt}
              </th>
              <th className="font-medium py-1 pr-2">{TEXT.columns.name}</th>
              <th className="font-medium py-1 pr-2 whitespace-nowrap">
                {TEXT.columns.observation}
              </th>
              {showSignal && (
                <th className="font-medium py-1 pr-2 whitespace-nowrap">
                  {HEIGHT_TEXT.columnSignal}{" "}
                  <Info text={HEIGHT_TEXT.signalInfo} />
                </th>
              )}
              <th className="font-medium py-1 pr-2 whitespace-nowrap">
                {TEXT.columns.jsd} <Info text={jsdInfo} />
              </th>
              <th className="font-medium py-1 pr-2 whitespace-nowrap">
                {TEXT.columns.reason} <Info text={reasonInfo} />
              </th>
              <th className="font-medium py-1 whitespace-nowrap">
                {TEXT.columns.rule} <Info text={TEXT.ruleInfo} />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const selected =
                r.linkedFeatureId !== null &&
                r.linkedFeatureId === selectedFeatureId;
              return (
                <tr
                  key={r.position}
                  data-testid="romsh-input-row"
                  data-selected={selected ? "true" : undefined}
                  className={`border-t border-gray-100 align-top ${selected ? "bg-blue-50" : ""}`}
                >
                  <td
                    className="text-gray-400 font-mono select-none"
                    aria-hidden="true"
                  >
                    {r.bracket}
                  </td>
                  <td className="py-1 pr-2 font-mono whitespace-nowrap">
                    <Data>{r.rtText}</Data>
                    {r.rtMissing && (
                      <span className="ml-1 text-[10px] text-gray-400">
                        {TEXT.rtMissing}
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-2 break-words max-w-[16rem]">
                    {r.linkedFeatureId !== null ? (
                      <button
                        type="button"
                        aria-pressed={selected}
                        onClick={() =>
                          onSelectFeature(r.linkedFeatureId as string)
                        }
                        className="text-left underline decoration-dotted underline-offset-2 hover:text-blue-700"
                      >
                        <Data>{r.compoundName}</Data>
                      </button>
                    ) : (
                      <Data>{r.compoundName}</Data>
                    )}
                    {r.dupCount > 1 && (
                      <span
                        className="ml-1 text-[10px] text-gray-400"
                        title={TEXT.dupSmilesInfo(r.dupCount)}
                      >
                        <WithData
                          text={TEXT.dupSmiles(r.dupCount)}
                          data={[`${r.dupCount}회`]}
                        />{" "}
                        <Info text={TEXT.dupSmilesInfo(r.dupCount)} />
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-2 whitespace-nowrap">
                    <Data>{r.observation}</Data>
                  </td>
                  {showSignal && (
                    <td className="py-1 pr-2 font-mono whitespace-nowrap text-right">
                      <Data>{r.signalText ?? TEXT.none}</Data>
                    </td>
                  )}
                  <td className="py-1 pr-2 font-mono whitespace-nowrap">
                    <Data>{r.jsdText}</Data>
                  </td>
                  <td className="py-1 pr-2 whitespace-nowrap">
                    {r.reasonText}
                  </td>
                  <td className="py-1 whitespace-nowrap">{r.ruleText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p
        data-testid="romsh-input-rule-guide"
        className="text-[11px] text-gray-600 leading-snug"
      >
        {TEXT.ruleGuide}
      </p>

      {summary && (
        <p
          data-testid="romsh-input-summary-values"
          className="text-[11px] text-gray-600 font-mono"
        >
          <span className="font-sans">{TEXT.summaryValuesLabel}</span>{" "}
          <Info text={summaryInfo} />{" "}
          {summary.map((s, i) => (
            <Fragment key={s.name}>
              {i > 0 && " · "}
              {/* 항목 안에서는 줄을 바꾸지 않는다(「플|랫폼」처럼 끊기지 않게) */}
              <span className="whitespace-nowrap">
                <Data>{`${s.name} ${s.value}`}</Data>
                {s.name === "env_id" && TEXT.envFixedSuffix}
              </span>
            </Fragment>
          ))}
        </p>
      )}

      <p className="text-[11px] text-gray-500">{TEXT.selectGuide}</p>
      <ul className="text-[11px] text-gray-500 leading-snug list-disc pl-4 space-y-0.5">
        <li data-testid="romsh-input-footnote1">
          {footnote1}
          {view.hasModelDecision && ` ${TEXT.footnote1End}`}
        </li>
        {showSignal && (
          <li data-testid="romsh-input-footnote-signal">
            {HEIGHT_TEXT.footnoteSignal}
          </li>
        )}
        <li>{TEXT.footnote2}</li>
      </ul>
    </section>
  );
}
